import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { createGUI, parametersGUI } from "./create-gui";

/**
 * Base
 */

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Galaxy
 */
let geometry = null;
let material = null;
let points = null;

const destroyOldGalaxy = () => {
  // Destroy old galaxy
  if (points !== null) {
    geometry.dispose();
    material.dispose();
    scene.remove(points);
  }
};

const parameters = parametersGUI;
const generateGalaxy = () => {
  destroyOldGalaxy();
  /**
   * Geometry
   */
  geometry = new THREE.BufferGeometry();

  const positions = new Float32Array(parameters.count * 3);
  const colors = new Float32Array(parameters.count * 3);

  const colorInside = new THREE.Color(parameters.insideColor);
  const colorOutside = new THREE.Color(parameters.outsideColor);

  for (let i = 0; i < parameters.count; i++) {
    // Position
    const i3 = i * 3;

    const randomiserRadius = Math.random() * parameters.radius;

    const spinAngle = randomiserRadius * parameters.spin;

    // To get a value btw 0 & 1
    const branchAngle =
      ((i % parameters.branches) / parameters.branches) * Math.PI * 2;

    const createRandom = () => {
      return (
        // randomnessPower will align the particles on the line if the value is hight
        // because of Math Pow (more pow level, less value since btw 0-1)
        Math.pow(Math.random(), parameters.randomnessPower) *
        // To get positive or negative value
        (Math.random() < 0.5 ? 1 : -1) *
        // The among of particule that will be dispersed in the range
        // less value -> particle will be compacted
        // big value -> particle will be dispersed a lot
        parameters.randomness *
        randomiserRadius
      );
    };

    const randomX = createRandom();
    const randomY = createRandom();
    const randomZ = createRandom();

    positions[i3] =
      Math.cos(branchAngle + spinAngle) * randomiserRadius + randomX;
    positions[i3 + 1] = randomY;
    positions[i3 + 2] =
      Math.sin(branchAngle + spinAngle) * randomiserRadius + randomZ;

    // Color
    // We need to clone mixedColor since Lerp update the reference of the variable
    const mixedColor = colorInside.clone();
    // Lerp will apply a color btw two ranges of colors, the second parameters is the value of this lerping
    // If 0 -> mixedColor = mixedColor
    // If 1 -> mixedColor = colorOutside
    // Otherwise -> Mixed color btw mixedColor & colorOutside
    mixedColor.lerp(colorOutside, randomiserRadius / parameters.radius);

    colors[i3] = mixedColor.r;
    colors[i3 + 1] = mixedColor.g;
    colors[i3 + 2] = mixedColor.b;
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  /**
   * Material
   */
  material = new THREE.PointsMaterial({
    size: parameters.size,
    sizeAttenuation: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
  });

  /**
   * Points
   */
  points = new THREE.Points(geometry, material);
  scene.add(points);
};

generateGalaxy();

createGUI(generateGalaxy);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 3;
camera.position.y = 3;
camera.position.z = 3;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
